import type { AuthChangeEvent, Session } from "@supabase/supabase-js";
import { useEffect, useRef } from "react";

import { meKeys } from "@/api/meKeys";
import { setAccessTokenHint } from "@/lib/accessTokenHint";
import { resetGoogleGis } from "@/lib/googleGis";
import { queryClient } from "@/lib/queryClient";
import { getLastActivityAt, isSessionIdle, touchActivity } from "@/lib/sessionActivity";
import { signOutClient } from "@/lib/signOut";
import { supabase } from "@/lib/supabase";
import { useAuthStore } from "@/store/authStore";

type AuthSyncRefs = {
  initialSessionMeSyncedRef: React.MutableRefObject<boolean>;
};

function sessionFromSupabase(session: Session) {
  return { id: session.user.id, email: session.user.email ?? "" };
}

function clearCacheIfUserChanged(nextUserId: string): void {
  const prevId = useAuthStore.getState().session?.id;
  if (prevId && prevId !== nextUserId) {
    queryClient.clear();
  }
}

function signOutIdleSession(refs: AuthSyncRefs): void {
  refs.initialSessionMeSyncedRef.current = false;
  void signOutClient().then(() => {
    window.location.replace("/login");
  });
}

function handleInitialSession(session: Session, refs: AuthSyncRefs): void {
  const { setLoadingAuth, setSession } = useAuthStore.getState();

  if (refs.initialSessionMeSyncedRef.current) {
    setLoadingAuth(false);
    return;
  }

  if (isSessionIdle()) {
    signOutIdleSession(refs);
    setLoadingAuth(false);
    return;
  }

  if (getLastActivityAt() === null) {
    touchActivity({ force: true });
  }

  refs.initialSessionMeSyncedRef.current = true;
  setSession(sessionFromSupabase(session));
  setAccessTokenHint(session.access_token);
  setLoadingAuth(false);
}

function handleSignedIn(session: Session, refs: AuthSyncRefs): void {
  const { setLoadingAuth, setSession } = useAuthStore.getState();

  // Supabase can emit SIGNED_IN again when the tab becomes visible — same user, no need to refetch /me.
  if (refs.initialSessionMeSyncedRef.current) {
    const s = useAuthStore.getState().session;
    const meData = queryClient.getQueryData(meKeys.detail());
    if ((s === null || s.id === session.user.id) && meData) {
      setLoadingAuth(false);
      return;
    }
  }

  clearCacheIfUserChanged(session.user.id);
  refs.initialSessionMeSyncedRef.current = true;
  touchActivity({ force: true });
  setSession(sessionFromSupabase(session));
  setAccessTokenHint(session.access_token);
  setLoadingAuth(false);
}

function handleAuthStateChange(
  event: AuthChangeEvent,
  session: Session | null,
  refs: AuthSyncRefs,
): void {
  const { setLoadingAuth, logout } = useAuthStore.getState();

  if (!session?.user) {
    refs.initialSessionMeSyncedRef.current = false;
    setAccessTokenHint(null);
    resetGoogleGis();
    queryClient.clear();
    logout();
    setLoadingAuth(false);
    return;
  }

  if (event === "TOKEN_REFRESHED") {
    setLoadingAuth(false);
    return;
  }

  if (event === "INITIAL_SESSION") {
    handleInitialSession(session, refs);
    return;
  }

  if (event === "USER_UPDATED") {
    useAuthStore.getState().setSession(sessionFromSupabase(session));
    setAccessTokenHint(session.access_token);
    void queryClient.invalidateQueries({ queryKey: meKeys.detail() });
    setLoadingAuth(false);
    return;
  }

  if (event === "SIGNED_IN") {
    handleSignedIn(session, refs);
    return;
  }

  if (event === "MFA_CHALLENGE_VERIFIED") {
    useAuthStore.getState().setSession(sessionFromSupabase(session));
    setAccessTokenHint(session.access_token);
    void queryClient.invalidateQueries({ queryKey: meKeys.detail() });
  }

  setLoadingAuth(false);
}

export function AuthSync(): null {
  /** After first successful INITIAL_SESSION handling for this login, ignore duplicate INITIAL_SESSION (tab focus). */
  const initialSessionMeSyncedRef = useRef(false);

  useEffect(() => {
    if (!supabase) {
      useAuthStore.getState().setLoadingAuth(false);
      return;
    }

    const refs: AuthSyncRefs = { initialSessionMeSyncedRef };

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event, session) => {
      handleAuthStateChange(event, session, refs);
    });

    return () => subscription.unsubscribe();
  }, []);

  return null;
}
